import axios from 'axios';
import {
	DateFilter,
	Job,
	JobApplication,
	JobApplicationDto,
	JobApplicationUpdateDto,
	JobAttendance,
	JobDto,
	JobOfflineAttendanceChange,
	JobSessionType,
	JobTimeslot,
	JobTimeslotInstance,
	JobValidations,
	QrCodeMap,
	TableItemsOptions,
} from '../../types';
import { fetchTableItems } from '.';
import {
	JobListType,
	JobStatusesInListType,
	PeriodConstraints,
} from '../../store/types';

export const jobApi = {
	async fetchJobTimeslotInstance(id: string): Promise<JobTimeslotInstance> {
		const { data } = await axios.get(`/job/ngo-timeslot-instances/${id}`);
		return data;
	},

	async fetchAttendanceQrCodes(
		timeslotInstanceId: string,
	): Promise<QrCodeMap[]> {
		const { data } = await axios.get(
			`/volunteer/ngo-job-attendances/qr-code-map`,
			{
				params: { timeslotInstanceId },
			},
		);
		return data;
	},

	async fetchAttendanceContactQrCodes(
		timeslotInstanceId: string,
	): Promise<QrCodeMap[]> {
		const { data } = await axios.get(
			`/volunteer/ngo-job-attendances/contact-qr-code-map`,
			{
				params: { timeslotInstanceId },
			},
		);
		return data;
	},

	async fetchJobAttendanceList(
		options: TableItemsOptions,
		timeslotInstanceId: string,
	) {
		const data = await fetchTableItems({
			...options,
			url: `volunteer/ngo-job-attendances?timeslotInstanceId=${timeslotInstanceId}`,
		});
		return data;
	},

	async fetchJobAttendanceListForExport({
		jobId,
		timeslotInstanceId,
		dateFormat,
		timeFormat,
	}: {
		jobId: string;
		timeslotInstanceId?: string;
		dateFormat: string;
		timeFormat: string;
	}) {
		const { data } = await axios.post(`volunteer/ngo-job-attendances/export`, {
			jobId,
			timeslotInstanceId,
			dateFormat,
			timeFormat,
		});

		return data;
	},

	async fetchJobApplicationListForExport(
		jobId: string,
		dateFormat: string,
		timeFormat: string,
	) {
		const { data } = await axios.post(`volunteer/ngo-job-applications/export`, {
			jobId,
			dateFormat,
			timeFormat,
		});

		return data;
	},

	async fetchJobAttendances(params: {
		timeslotInstanceId?: string;
		ngoId?: number;
		userId?: string;
		fullList?: boolean;
		dateFilter?: DateFilter;
	}): Promise<JobAttendance[]> {
		const { data } = await axios.get(`volunteer/ngo-job-attendances`, {
			params,
		});
		return data;
	},

	async saveJobAttendance(
		changes: JobOfflineAttendanceChange[],
	): Promise<{ failed?: string[]; succeeded: string[] }> {
		try {
			const { data } = await axios.put(`/volunteer/ngo-job-attendances/bulk`, {
				changes,
			});
			return data;
		} catch {
			// assume all failed
			return { succeeded: [] };
		}
	},

	async createJobApplication(
		application: JobApplicationDto,
	): Promise<JobApplication> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-applications`,
			application,
		);
		return data;
	},

	async checkin(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/checkin`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async checkout(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/checkout`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async undoCheckIn(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/undo-check-in`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async undoCheckOut(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/undo-check-out`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async confirmHours(
		attendanceId: string,
		hours: number,
	): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/confirm-hours`,
			{
				attendanceId,
				hours,
			},
		);
		return data;
	},

	async confirm(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/confirm`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async absent(
		attendanceId: string,
		absentReason: string,
	): Promise<JobAttendance> {
		const { data } = await axios.post(`/volunteer/ngo-job-attendances/absent`, {
			attendanceId,
			absentReason,
		});
		return data;
	},

	async unconfirm(attendanceId: string): Promise<JobAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/unconfirm`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async fetchJobTableItems(
		options: TableItemsOptions,
		listType: JobListType,
		progress?: string | string[],
		reportId?: string,
		periodConstraints?: PeriodConstraints,
		centerIds?: number[],
	): Promise<any> {
		const baseUrl = '/job/jobs';
		const params = new URLSearchParams();

		JobStatusesInListType[listType].forEach(s => {
			params.append('statuses', s);
		});

		if (progress) {
			if (typeof progress === 'string') {
				params.set('progresses', progress);
			} else {
				progress.forEach(p => {
					params.append('progresses', p);
				});
			}
		}

		if (centerIds) {
			centerIds.forEach(id => {
				params.append('centerIds', id.toString());
			});
		}

		if (reportId) {
			params.append('reportId', reportId);
		}
		if (periodConstraints) {
			params.append('periodInclusive', periodConstraints.inclusive.toString());
			if (periodConstraints.start) {
				params.append('periodStart', periodConstraints.start.toString());
			}
			if (periodConstraints.end) {
				params.append('periodEnd', periodConstraints.end.toString());
			}
		}

		const data = await fetchTableItems({
			...options,
			url: `${baseUrl}?${params.toString()}`,
		});
		return data;
	},

	async fetchPresetQuestions({
		centerId,
		type,
	}: {
		centerId: number;
		type: string;
	}): Promise<any[]> {
		const { data } = await axios.get(
			`/job/preset-questions/?centerId=${centerId}&type=${type}`,
		);
		return data;
	},

	async fetchJobs(body: { ids: string[] }): Promise<Job[]> {
		const { data } = await axios.post(`/job/jobs/get-filtered`, body);
		return data;
	},

	async fetchJob(id: string): Promise<Job> {
		const { data } = await axios.get(`/job/jobs/${id}`);
		return data;
	},

	async fetchJobTimeslotById(id: string): Promise<any> {
		const { data } = await axios.get(`/job/timeslots/${id}`);
		return data;
	},

	async fetchJobTimeslots(
		options: TableItemsOptions,
		jobId: string,
		sessionId: string,
	): Promise<any> {
		const url = `/job/jobs/${jobId}/timeslots?sessionId=${sessionId}`;

		const data = await fetchTableItems({
			...options,
			url: url,
		});
		return data;
	},

	async createJob(job: JobDto, validations: JobValidations): Promise<Job> {
		const { data } = await axios.post(`/job/jobs`, { ...job, validations });
		return data;
	},

	async updateJob(
		id: string,
		job: JobDto,
		validations: JobValidations,
	): Promise<Job> {
		const { data } = await axios.put(`/job/jobs/${id}`, {
			...job,
			validations,
		});
		return data;
	},

	async appendTimeslots({
		jobId,
		sessionId,
		timeslots,
	}: {
		jobId: string;
		sessionId: string;
		timeslots: JobTimeslot[];
	}): Promise<{ failed?: string[]; succeeded: string[] }> {
		const { data } = await axios.post(
			`/job/jobs/${jobId}/sessions/${sessionId}/timeslots`,
			{
				timeslots,
			},
		);
		return data;
	},

	async fetchJobSessionTypes(): Promise<JobSessionType[]> {
		const { data } = await axios.get(`/job/session-types`);
		return data;
	},

	async fetchJobTimeslotInstances(body: {
		timeslotIds: string[];
		dateFilter?: DateFilter;
	}): Promise<JobTimeslotInstance[]> {
		const { data } = await axios.post(
			`/job/ngo-timeslot-instances/get-filtered`,
			body,
		);
		return data;
	},

	async fetchJobApplicationTableItems(
		options: TableItemsOptions,
		jobId: string,
	): Promise<any> {
		const params = {
			...options,
			url: `job/jobs/${jobId}/applications`,
		};
		return fetchTableItems(params);
	},

	async fetchJobApplicationsByUserId({
		userId,
	}: {
		userId: string;
	}): Promise<JobApplication[]> {
		const { data } = await axios.get(`volunteer/ngo-job-applications`, {
			params: { userIds: [userId] },
		});
		return data;
	},

	async fetchJobApplications({
		jobId,
		userIds,
		excludeCancelled = true,
	}: {
		jobId: string;
		userIds: string[];
		excludeCancelled?: boolean;
	}): Promise<JobApplication[]> {
		const { data } = await axios.get(`volunteer/ngo-job-applications`, {
			params: { jobId, userIds, excludeCancelled },
		});
		return data;
	},

	async fetchJobApplicationById(
		applicationId: string,
	): Promise<JobApplication> {
		const { data } = await axios.get(
			`/volunteer/ngo-job-applications/${applicationId}`,
		);
		return data;
	},

	async updateJobApplication(
		applicationId: string,
		application: Partial<JobApplicationUpdateDto>,
	): Promise<JobApplication> {
		const { data } = await axios.put(
			`/volunteer/ngo-job-applications/${applicationId}`,
			application,
		);
		return data;
	},

	async updateJobAppliConsent(applicationId: string, code: string) {
		const {
			data,
		} = await axios.post(
			`/volunteer/ngo-job-applications/${applicationId}/consent`,
			{ code },
		);
		return data;
	},

	async updateJobAttendanceRemarks(attendanceId: string, remarks: string) {
		const { data } = await axios.post(
			`/volunteer/ngo-job-attendances/${attendanceId}/update-remarks`,
			{
				remarks,
			},
		);
		return data;
	},
};
